import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatDay } from '../../utils/format';

const FACILITY_COLORS = [
    '#6366f1',
    '#10b981', 
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#ec4899'
];

function DurationLineChart({ runs, compact = false, onFacilityClick = null, onDateClick = null, selectedDate = '' }) {
    // Filter to only Complete runs and group by date and facility
    const completeRuns = runs.filter(r => r.status === 'Complete');
    
    // Get unique facilities
    const facilities = [...new Set(completeRuns.map(r => r.facilityID))];
    
    // Group runs by date
    const runsByDate = completeRuns.reduce((acc, run) => {
        const date = formatDay(run.startTime);
        if (!acc[date]) {
            acc[date] = { date };
        }
        // If multiple runs per facility per day, take the average
        if (!acc[date][run.facilityID]) {
            acc[date][run.facilityID] = [];
        }
        acc[date][run.facilityID].push(run.totalMinutes || 0);
        return acc;
    }, {});

    // Convert to chart data with averages
    const data = Object.values(runsByDate)
        .map(dayData => {
            const result = { date: dayData.date };
            facilities.forEach(facility => {
                if (dayData[facility] && dayData[facility].length > 0) {
                    const avg = dayData[facility].reduce((a, b) => a + b, 0) / dayData[facility].length;
                    result[facility] = Math.round(avg * 100) / 100;
                }
            });
            return result;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    if (data.length === 0) {
        return <div className="chart-empty">No completed runs to display</div>;
    }

    // Shorten facility names for legend
    const shortenFacility = (name) => {
        const match = name.match(/\d+$/);
        return match ? match[0] : name.slice(-6);
    };

    const handleClick = (data) => {
        if (onDateClick && data && data.activeLabel) {
            onDateClick(data.activeLabel);
        }
    };

    const handleLegendClick = (e) => {
        if (onFacilityClick && e && e.value) {
            onFacilityClick(shortenFacility(e.value));
        }
    };

    return (
        <div className={`chart-container ${compact ? 'compact' : ''}`}>
            {!compact && <h3 className="chart-title">Run Duration Over Time</h3>}
            <ResponsiveContainer width="100%" height={compact ? 220 : 350}>
                <LineChart 
                    data={data} 
                    margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    onClick={handleClick}
                    style={{ cursor: onDateClick ? 'pointer' : 'default' }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                        dataKey="date" 
                        stroke="#a1a1aa"
                        tick={{ fill: '#a1a1aa', fontSize: compact ? 10 : 12 }}
                        tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getMonth() + 1}/${date.getDate()}`;
                        }}
                    />
                    <YAxis 
                        stroke="#a1a1aa"
                        tick={{ fill: '#a1a1aa', fontSize: compact ? 10 : 12 }}
                        label={!compact ? { value: 'Minutes', angle: -90, position: 'insideLeft', fill: '#a1a1aa' } : null}
                    />
                    <Tooltip 
                        contentStyle={{ 
                            background: 'rgba(26, 26, 46, 0.95)', 
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px',
                            color: '#e4e4e7'
                        }}
                        formatter={(value) => [`${value} min`, '']}
                    />
                    <Legend 
                        wrapperStyle={{ color: '#e4e4e7', fontSize: compact ? '11px' : '14px' }}
                        formatter={(value) => <span style={{ color: '#e4e4e7' }}>{shortenFacility(value)}</span>}
                        onClick={handleLegendClick}
                    />
                    {facilities.map((facility, index) => (
                        <Line
                            key={facility}
                            type="monotone"
                            dataKey={facility}
                            name={facility}
                            stroke={FACILITY_COLORS[index % FACILITY_COLORS.length]}
                            strokeWidth={2}
                            dot={{ 
                                r: compact ? 2 : 4,
                                fill: FACILITY_COLORS[index % FACILITY_COLORS.length]
                            }}
                            activeDot={{ 
                                r: 6, 
                                stroke: '#fff', 
                                strokeWidth: 2,
                                cursor: 'pointer'
                            }}
                            connectNulls
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
            {(onDateClick || onFacilityClick) && !compact && (
                <p className="chart-click-hint">Click a point to filter by date, click legend to filter by facility</p>
            )}
        </div>
    );
}

export default DurationLineChart;
